import {
  create,
} from 'zustand';
import {
  ClientSticky,
  StickyApi,
  StickyDraft,
  PendingSave,
} from 'views/EMR/Sticky/types';
import {
  applyCreateSuccessState,
  applyDraftPatchState,
  applySignalRUpsertState,
  applyUpdateSuccessState,
  createInitialDraftState,
  getDraftStickiesForPendingSaves,
  initialStorageState,
  isValidDraft,
  markSaveErrorState,
  markSavingState,
  normalizeStickiesForPatient,
  removeStickyLocalState,
  resetDraftToPersistedState,
  setLoadingState,
  StickyNotesCoreState,
} from 'views/EMR/Sticky/stores/mutations';
import {
  createSaveQueue,
} from 'views/EMR/Sticky/stores/saveQueue';
import {
  createTemporaryStickyId,
  isTemporaryStickyId,
} from 'views/EMR/Sticky/stores/constants';
import {
  sortStickyIds,
} from 'views/EMR/Sticky/utils';
import isNil from 'lodash/isNil';
import {
  StickyNotificationDTO,
} from 'dtos';
import {
  uuid,
} from 'utils/misc';

type StickyNotesState = StickyNotesCoreState & {
  // injection points
  api: StickyApi | null;
  clientId: string | null;
  focusRequestedStickyId: number | null;

  actions: {
    setApi: (api: StickyApi) => void;
    setClientId: (clientId: string) => void;

    setActivePatient: (patientId: number) => Promise<void>;
    resetStickyCoreStates: () => void;

    prepareDraftForEditing: (stickyId: number) => void;
    updateDraft: (stickyId: number, patch: Partial<StickyDraft>) => void;

    scheduleSave: (stickyId: number) => void;
    flushSave: (stickyId: number) => Promise<void>;

    handleDraftStickiesForOldPatient: () => void;
    processPendingSaves: () => Promise<void>;

    createSticky: () => void;
    deleteSticky: (stickyId: number) => Promise<void>;
    togglePin: (stickyId: number, value: boolean) => Promise<void>;

    setEditingStickyUniqueId: (uniqueId: string | null) => void;
    applySignalRNotification: (dto: StickyNotificationDTO) => Promise<void>;

    syncWithRemote: (stickyId: number) => void;
    ignoreRemoteUpdate: (stickyId: number) => Promise<void>;

    requestFocusSticky: (stickyId: number) => void;
    clearFocusStickyRequest: () => void;
  };
};

const useStickyNotesStore = create<StickyNotesState>((set, get) => {
  // Queue encapsulates timers/inFlight but preserves behavior exactly
  const saveQueue = createSaveQueue({
    flush: async (stickyId) => {
      await get().actions.flushSave(stickyId);
    },
  });

  return {
    activePatientId: null,
    persistedById: {},
    orderIds: [],
    isStickyNotesLoading: false,
    draftById: {},
    metaById: {},
    pendingSaves: {},
    editingStickyUniqueId: null,
    api: null,
    clientId: null,
    focusRequestedStickyId: null,

    actions: {
      setApi: (api) => set({ api }),
      setClientId: (clientId) => set({ clientId }),
      requestFocusSticky: (stickyId) => set({ focusRequestedStickyId: stickyId }),
      clearFocusStickyRequest: () => set({ focusRequestedStickyId: null }),

      setActivePatient: async (patientId) => {
        const { api, actions } = get();
        const {
          handleDraftStickiesForOldPatient,
          resetStickyCoreStates,
          processPendingSaves,
        } = actions;
        const {
          fetchStickies,
        } = api ?? {};

        if (!isNil(fetchStickies)) {
          // instant switch: hand off dirty drafts (best-effort background)
          handleDraftStickiesForOldPatient();

          // clear workspace immediately
          resetStickyCoreStates();
          set({ activePatientId: patientId });

          set(setLoadingState(true));

          // load new patient
          const list = await fetchStickies(patientId);
          const updatedStates = normalizeStickiesForPatient(patientId, list as ClientSticky[]);

          set(updatedStates);

          // background: process pending saves (previous patient) if any
          processPendingSaves();
        } else {
          console.error('Sticky ERROR: setActivePatient: API not set');
        }
      },

      resetStickyCoreStates: () => {
        saveQueue.cancelAll();
        set({
          ...initialStorageState,
          focusRequestedStickyId: null,
        });
      },

      prepareDraftForEditing: (stickyId) => {
        const patch = createInitialDraftState(get(), stickyId);
        if (!isNil(patch)) {
          set(patch);
        }
      },

      updateDraft: (stickyId, patch) => {
        const stateBefore = get();
        const patchState = applyDraftPatchState(stateBefore, stickyId, patch);
        if (!patchState) return;

        set(patchState);

        // decide scheduling based on the computed current draft/meta
        const { draftById, metaById, actions } = get();
        const draft = draftById[stickyId];
        const meta = metaById[stickyId];
        const dirty = !!meta?.dirty;
        if (!draft) return;

        const savable = isValidDraft(draft);

        // Update blocked state based on savability
        if (dirty && !savable && meta?.blocked !== 'validation') {
          set({
            metaById: {
              ...get().metaById,
              [stickyId]: {
                ...get().metaById[stickyId],
                blocked: 'validation',
              },
            },
          });
        } else if (savable && meta?.blocked) {
          set({
            metaById: {
              ...get().metaById,
              [stickyId]: {
                ...get().metaById[stickyId],
                blocked: null,
              },
            },
          });
        }

        const shouldSchedule = dirty && isValidDraft(draft);
        if (shouldSchedule) {
          actions.scheduleSave(stickyId);
        }
      },

      scheduleSave: (stickyId) => {
        saveQueue.schedule(stickyId);
      },

      flushSave: async (stickyId) => {
        const {
          api,
          activePatientId,
          draftById,
          persistedById,
          metaById,
          clientId,
        } = get();

        if (!api) throw new Error('StickyNotesStore API not set');
        if (!activePatientId) return;
        if (!clientId) throw new Error('StickyNotesStore clientId not set');

        // cancel debounce timer if any
        saveQueue.clearTimerIfAny(stickyId);

        // serialize saves per sticky
        if (saveQueue.isInFlight(stickyId)) {
          return;
        }

        const persisted = persistedById[stickyId];
        const draft = draftById[stickyId];
        if (!persisted || !draft) return;

        const meta = metaById[stickyId];

        // no-op guard
        if (!meta?.dirty || meta?.hasRemoteUpdateWhileEditing) return;

        if (!isNil(meta?.error)) return;

        // Gate autosave: require both title and content for all stickies
        if (!isValidDraft(draft)) {
          set({
            metaById: {
              ...get().metaById,
              [stickyId]: {
                ...get().metaById[stickyId],
                blocked: 'validation',
              },
            },
          });
          return;
        }

        // Clear blocked state since draft is savable
        if (meta?.blocked) {
          set({
            metaById: {
              ...get().metaById,
              [stickyId]: {
                ...get().metaById[stickyId],
                blocked: null,
              },
            },
          });
        }

        const isNew = isTemporaryStickyId(stickyId);

        try {
          saveQueue.markInFlight(stickyId);
          set(markSavingState(get(), stickyId));

          if (isNew) {
            // CREATE: call addSticky
            const newSticky = await api.addSticky(activePatientId, draft, clientId);

            // Get the uniqueId from the temp sticky before removing it
            const tempSticky = get().persistedById[stickyId];
            const uniqueId = tempSticky?.uniqueId;

            set(applyCreateSuccessState({
              state: get(),
              tempStickyId: stickyId,
              newSticky: newSticky as ClientSticky,
              preservedUniqueId: uniqueId,
            }));
          } else {
            // UPDATE: call editSticky
            const updated = await api.editSticky(
              activePatientId,
              stickyId,
              draft,
              clientId,
            );

            set(applyUpdateSuccessState({
              state: get(),
              stickyId,
              updated: updated as ClientSticky,
            }));
          }
        } catch (e: any) {
          set(markSaveErrorState(get(), stickyId, e?.message ?? 'Failed to save'));
        } finally {
          saveQueue.clearInFlight(stickyId);

          // If user changed draft again while we were saving, meta.dirty may be true; reschedule
          // But only if the sticky still exists (wasn't replaced by real ID)
          const currentMeta = get().metaById[stickyId];
          if (currentMeta?.dirty && !currentMeta?.error) {
            get().actions.scheduleSave(stickyId);
          }
        }
      },
      handleDraftStickiesForOldPatient: () => {
        const patch = getDraftStickiesForPendingSaves(get());
        if (!isNil(patch)) {
          set(patch);
          // cancel timers for old workspace stickies
          saveQueue.cancelAll();
        }
      },
      processPendingSaves: async () => {
        const {
          api,
          pendingSaves,
          clientId,
        } = get();
        const pendingSavesEntries = Object.entries(
          pendingSaves as Record<string, PendingSave>,
        );

        if (isNil(api) || isNil(clientId)) {
          console.error('StickyNotesStore API or clientId not set', { api, clientId });
          return;
        }

        if (pendingSavesEntries.length > 0) {
          const { addSticky, editSticky } = api;
          // Sequential processing using reduce for async iteration
          await pendingSavesEntries.reduce(async (prevPromise, [key, item]) => {
            await prevPromise;
            try {
              const isNew = isTemporaryStickyId(item.stickyId);
              if (isNew) {
                await addSticky(item.patientId, item.payload, clientId);
              } else {
                await editSticky(item.patientId, item.stickyId, item.payload, clientId);
              }

              // remove on success
              const next = { ...get().pendingSaves };
              delete next[key];
              set({ pendingSaves: next });
            } catch {
              console.error('StickyNotesStore error processing pending save', { key, item });
            }
          }, Promise.resolve());
        }
      },

      createSticky: () => {
        const {
          activePatientId,
          persistedById,
          orderIds,
          draftById,
          metaById,
          actions,
        } = get();

        if (!isNil(activePatientId)) {
          const existingTempId = orderIds.find((id) => isTemporaryStickyId(id));
          if (existingTempId !== undefined) {
            actions.requestFocusSticky(existingTempId);
            return;
          }

          const tempId = createTemporaryStickyId();
          const uniqueId = uuid();
          const now = new Date().toISOString();

          // Create a local "persisted" record with temp ID
          const newSticky: ClientSticky = {
            stickyId: tempId,
            patientId: activePatientId,
            uniqueId, // Stable identifier for React key
            title: '',
            content: '',
            isPinned: false,
            createdOn: now,
            updatedOn: now,
          };

          const nextPersistedById = { ...persistedById, [tempId]: newSticky };

          set({
            persistedById: nextPersistedById,
            orderIds: sortStickyIds(nextPersistedById),
            draftById: {
              ...draftById,
              [tempId]: { title: '', content: '', isPinned: false },
            },
            metaById: {
              ...metaById,
              [tempId]: { dirty: false, saving: false, error: null },
            },
          });
        } else {
          console.error('StickyNotesStore activePatientId not set');
        }
      },

      deleteSticky: async (stickyId) => {
        const { api, activePatientId, clientId } = get();

        const removeFromLocalState = () => {
          // Cancel any pending timer + in-flight for this sticky
          saveQueue.cancel(stickyId);

          const updatedState = removeStickyLocalState({ state: get(), stickyId });
          set(updatedState);
        };

        // For temp stickies (not yet saved to server), just remove locally
        if (isTemporaryStickyId(stickyId)) {
          removeFromLocalState();
        } else if (!isNil(api) && !isNil(activePatientId) && !isNil(clientId)) {
          await api.deleteSticky(activePatientId, stickyId, clientId);
          removeFromLocalState();
        } else {
          console.error('StickyNotesStore Delete failed: API or activePatientId or clientId not set', { api, activePatientId, clientId });
        }
      },

      togglePin: async (stickyId, value) => {
        const { persistedById, actions } = get();
        const persisted = persistedById[stickyId];
        if (!isNil(persisted)) {
          actions.updateDraft(stickyId, { isPinned: value });
          await actions.flushSave(stickyId);
        }
      },

      setEditingStickyUniqueId: (uniqueId) => {
        const prev = get().editingStickyUniqueId;
        set({ editingStickyUniqueId: uniqueId });

        // When editing ends (null), trigger re-sort
        if (prev !== null && uniqueId === null) {
          const { persistedById } = get();
          set({ orderIds: sortStickyIds(persistedById) });
        }
      },

      applySignalRNotification: async (dto) => {
        const { api, activePatientId, clientId } = get();
        if (!isNil(api)) {
          const patientId = dto.patientId ?? null;
          const stickyId = dto.stickyId ?? null;
          const op = dto.operationType ?? null;
          const origin = dto.clientId ?? null;

          if (isNil(patientId) || isNil(stickyId) || isNil(op)) return;

          // Only care about active patient
          if (isNil(activePatientId) || patientId !== activePatientId) return;

          // Ignore same-tab echo
          if (!isNil(origin) && !isNil(clientId) && origin === clientId) return;

          if (op === 'delete') {
            const updatedState = removeStickyLocalState({ state: get(), stickyId });
            set(updatedState);
            return;
          }

          // create/update: fetch sticky and merge
          const fresh = await api.fetchSticky(patientId, stickyId);
          const updatedState = applySignalRUpsertState({
            state: get(),
            stickyId,
            fresh: fresh as ClientSticky,
          });
          set(updatedState);

          // If user is editing locally and dirty, flag remote update
          const {
            editingStickyUniqueId,
            metaById,
            persistedById,
          } = get();
          const meta = metaById[stickyId];
          const persisted = persistedById[stickyId];

          if (meta?.dirty || editingStickyUniqueId === persisted?.uniqueId) {
            set({
              metaById: {
                ...get().metaById,
                [stickyId]: { ...meta, hasRemoteUpdateWhileEditing: true },
              },
            });
          }
        }
      },
      syncWithRemote: (stickyId) => {
        const state = get();
        const patch = resetDraftToPersistedState(state, stickyId);
        set(patch);

        // Cancel any pending save timer
        saveQueue.cancel(stickyId);
      },

      ignoreRemoteUpdate: async (stickyId) => {
        const state = get();
        const meta = state.metaById[stickyId];

        if (!meta) return;

        // Clear the remote update flag but keep dirty true to allow save
        set({
          metaById: {
            ...get().metaById,
            [stickyId]: {
              ...get().metaById[stickyId],
              hasRemoteUpdateWhileEditing: false,
              dirty: true,
            },
          },
        });

        // Now flush the save
        await get().actions.flushSave(stickyId);
      },
    },
  };
});

// -------------------- Selector hooks (same exports as you had) --------------------
export const useActivePatientId = () => useStickyNotesStore(
  (state) => state.activePatientId,
);

export const useStickyActions = () => useStickyNotesStore(
  (state) => state.actions,
);

export const usePersistedSticky = (stickyId: number) => useStickyNotesStore(
  (state) => state.persistedById[stickyId],
);

export const useStickyMeta = (stickyId: number) => useStickyNotesStore(
  (state) => state.metaById[stickyId],
);

export const useStickyDraft = (stickyId: number) => useStickyNotesStore(
  (state) => state.draftById[stickyId],
);

export const useOrderedStickyIds = () => useStickyNotesStore(
  (state) => state.orderIds,
);

export const useStickyNotesLoading = () => useStickyNotesStore(
  (state) => state.orderIds.length === 0
    && state.activePatientId !== null
    && state.isStickyNotesLoading,
);

export const useEditingStickyUniqueId = () => useStickyNotesStore(
  (state) => state.editingStickyUniqueId,
);

export const usePersistedStickies = () => useStickyNotesStore(
  (state) => state.persistedById,
);

export const useFocusRequestedStickyId = () => useStickyNotesStore(
  (state) => state.focusRequestedStickyId,
);

export default useStickyNotesStore;
